import React, { useState, useMemo, useEffect, useCallback } from "react";
import Table from "../../components/ReactTable/Table";
import usePagination from "../../components/ReactTable/usePagination";
import TooltipButton from "../../components/Buttons/TooltipButton";
import CourseTypeIcon from "../../components/Icons/CourseTypeIcon";
import { enqueueSuccessSnackbar } from "../../redux/actions/snackbarActions";
import { courseLocationTypes } from "../../constants";
import WarningModal from "../../components/WarningModal/WarningModal";
import TextFilter from "../../components/ReactTable/Filters/TextFilter";
import usePermissions from "../../redux/selectors/usePermissions";
import { isEqual } from "lodash";
import { useDispatch } from "react-redux";

import useResource from "../../hooks/useResource";
import { useTrainingObjects } from "../../hooks/apiHooks/trainingObjects";
import { useUserAssignments } from "../../hooks/apiHooks/userAssignments";
import useScopedTranslation from "../../hooks/useScopedTranslation";
import useModal from "../../hooks/useModal";

const USER_ASSIGNMENT_JSONAPI_REQUEST = {
  fields: {
    userAssignment: [
      "trainingObject",
      "assignmentType",
      "assignedBy",
      "assignment",
      "enrolledByUser",
      "enrolledCtoa",
      "user"
    ],
    user: ["fullName", "permissions"],
    assignment: ["assignmentType"],
    assignmentType: ["name", "description"],
    customerAssignment: ["trainingObject"],
    trainingObject: ["id", "name", "dateTime"]
  },
  include: [
    "trainingObject",
    "assignmentType",
    "assignedBy",
    "enrolledByUser",
    "enrolledCtoa",
    "enrolledCtoa.trainingObject",
    "assignment",
    "assignment.creator",
    "assignment.assignmentType"
  ]
};

const TRAINING_OBJECT_JSONAPI_REQUEST = {
  fields: {
    trainingObject: [
      "trainingObjectType",
      "trainingLocation",
      "parent",
      "name",
      "dateTime",
      "locationTypeString",
      "locationString"
    ]
  },
  include: ["trainingObjectType", "parent", "trainingLocation"]
};

const useIlts = employeeId => {
  const { getTrainingObjects } = useTrainingObjects(
    TRAINING_OBJECT_JSONAPI_REQUEST
  );
  const { getUserAssignments, updateUserAssignment } = useUserAssignments();

  const mapFilters = useCallback(filters => {
    return filters.reduce((mappedFilter, filter) => {
      mappedFilter[filter.id] = filter.value;
      return mappedFilter;
    }, {});
  }, []);

  const enroll = useCallback(
    async (userAssignment, trainingObject, filters, sortBy, page) => {
      await updateUserAssignment(
        userAssignment?.id,
        {
          action: "enroll",
          trainingObjectId: trainingObject.id,
          userId: employeeId
        },
        { fields: ["id"] }
      );
      return await getIlts(filters, sortBy, page);
    },
    []
  );

  const unenroll = useCallback(
    async (userAssignment, filters, sortBy, page) => {
      await updateUserAssignment(
        userAssignment.id,
        { action: "unenroll" },
        { fields: ["id"] }
      );
      return await getIlts(filters, sortBy, page);
    },
    []
  );

  const getIlts = useCallback(
    async (filters = [], sortBy, newPage) => {
      const {
        data: trainingObjects,
        totalCount,
        page
      } = await getTrainingObjects({
        filters: {
          ...mapFilters(filters),
          iltsAvailableForUser: employeeId
        },
        sortBy,
        page: newPage
      });

      const { data: userAssignments } = await getUserAssignments({
        filters: {
          users: [employeeId],
          trainingObjects: trainingObjects.map(to => to.parent.id),
          active: true
        },
        ...USER_ASSIGNMENT_JSONAPI_REQUEST
      });

      const courseAssignmentsHash = userAssignments.reduce((hash, ua) => {
        const id = ua.trainingObject.id;
        hash[id] = ua;
        return hash;
      }, {});

      const courses = trainingObjects.map(course => ({
        course,
        ua: courseAssignmentsHash[course.parent.id]
      }));
      return { data: courses, totalCount, page };
    },
    [mapFilters, employeeId]
  );

  return { getIlts, enroll, unenroll };
};

const makeCommentFromUa = (ua, course, t) => {
  if (!ua) {
    return <></>;
  }

  const hasEnrollment =
    ua.enrolledByUser && ua.enrolledCtoa?.trainingObject.id === course.id;
  const lines = [];

  if (
    hasEnrollment ||
    (!ua.enrolledCtoa &&
      !["Enrolled", "Self Assigned"].includes(ua.assignmentType.name))
  ) {
    lines.push(ua.assignmentType.description);
  }

  if (
    ua.assignedBy &&
    (hasEnrollment ||
      (!ua.enrolledCtoa &&
        !["Enrolled", "Self Assigned"].includes(ua.assignmentType.name)))
  ) {
    lines.push(`${t("comments.assignedBy")}: ${ua.assignedBy.fullName}`);
  }
  if (hasEnrollment) {
    lines.push(`${t("comments.enrolledBy")}: ${ua.enrolledByUser.fullName}`);
  }

  return (
    <>
      {lines.map((line, index) => (
        <div key={index}>{line}</div>
      ))}
    </>
  );
};

const makeLocationString = (course, t) => {
  if (course.locationTypeString !== courseLocationTypes.inPerson) {
    return t("iltLocationType." + course.locationString);
  }

  const [cityState, ...name] = course.locationString.split("|").reverse();
  return (
    <>
      {name
        .reverse()
        .join("|")
        .trim()}
      <br />
      {cityState}
    </>
  );
};

const columns = t => [
  {
    Header: t("type.header"),
    accessor: "iltType",
    id: "iltType",
    disableSortBy: false
  },
  {
    Header: t("name.header"),
    accessor: "name",
    id: "name",
    Filter: TextFilter,
    filterPlaceholder: t("name.filterPlaceholder")
  },
  {
    Header: t("location.header"),
    accessor: "location",
    id: "location",
    Filter: TextFilter,
    filterPlaceholder: t("location.filterPlaceholder")
  },
  {
    Header: t("dateTime.header"),
    accessor: "dateTime",
    id: "dateTime"
  },
  {
    Header: t("comments.header"),
    accessor: "comments",
    id: "comments",
    disableSortBy: true
  },
  {
    Header: t("action.header"),
    accessor: "action",
    id: "action",
    disableSortBy: true
  }
];

const EnrollModal = ({ onResult, warningMessage }) => {
  return (
    <WarningModal
      open={true}
      onClose={() => onResult(false)}
      onSubmit={() => onResult(true)}
      warningMessage={warningMessage}
    />
  );
};

const UnenrollModal = ({ onResult, warningMessage }) => {
  return (
    <WarningModal
      open={true}
      onClose={() => onResult(false)}
      onSubmit={() => onResult(true)}
      warningMessage={warningMessage}
    />
  );
};

const ActionButton = ({ ua, course, enroll, unenroll }) => {
  const { isAdmin } = usePermissions();
  const openEnrollModal = useModal(EnrollModal);
  const openUnenrollModal = useModal(UnenrollModal);
  const t = useScopedTranslation("employees.ilt.table.columns");
  const globalT = useScopedTranslation("employees.ilt");

  const shouldUnenroll =
    ua && ua.enrolledCtoa && ua.enrolledCtoa.trainingObject.id === course.id;
  const canPerformAction =
    isAdmin ||
    !ua?.enrolledByUser?.permissions?.data?.attributes?.isAdmin ||
    !ua?.enrolledCtoa;
  let action = () => {};

  if (canPerformAction) {
    if (shouldUnenroll) {
      action = async () => {
        const result = await openUnenrollModal({
          warningMessage: globalT("warnings.shouldUnenroll", {
            course: course.name
          })
        }).result;
        if (result) {
          unenroll();
        }
      };
    } else {
      action = async () => {
        if (ua && ua.enrolledCtoa) {
          if (
            !(await openEnrollModal({
              warningMessage: globalT("warnings.alreadyEnrolled", {
                course: course.name,
                dateTime: course.dateTime
              })
            }).result)
          ) {
            return;
          }
        }
        enroll();
      };
    }
  }

  let tooltipMessage = "";

  if (!canPerformAction) {
    if (shouldUnenroll) {
      tooltipMessage = t("action.tooltips.unenrollNotPermitted");
    } else {
      if (ua && ua.enrolledCtoa) {
        tooltipMessage = t("action.tooltips.reenrollNotPermitted", {
          course: ua.enrolledCtoa.trainingObject.name,
          dateTime: ua.enrolledCtoa.trainingObject.dateTime
        });
      } else {
        tooltipMessage = t("action.tooltips.unenrollNotPermitted");
      }
    }
  }

  return (
    <TooltipButton
      onClick={action}
      variant="contained"
      color="primary"
      disabled={!canPerformAction}
      tooltipProps={{
        title: tooltipMessage
      }}
    >
      {shouldUnenroll ? t("action.unenroll") : t("action.enroll")}
    </TooltipButton>
  );
};

const IltTable = ({ api }) => {
  const [
    { data: courses, totalCount = 0 },
    { enroll, unenroll, getIlts },
    currentAction
  ] = useResource(api);

  const [filters, setFilters] = useState([]);
  const [sortBy, setSortBy] = useState(["name", "dateTime"]);
  const t = useScopedTranslation("employees.ilt.table.columns");
  const globalT = useScopedTranslation("employees.ilt");
  const pagination = usePagination({
    totalCount
  });

  const memoColumns = useMemo(() => columns(t));

  useEffect(() => {
    getIlts(filters, sortBy, {
      size: pagination.pageSize,
      number: pagination.page
    });
  }, [
    JSON.stringify({
      filters,
      sortBy,
      pageSize: pagination.pageSize,
      pageNumber: pagination.page
    })
  ]);

  const enrollAction = useCallback(
    (ua, course) => {
      return () =>
        enroll(ua, course, filters, sortBy, {
          size: pagination.pageSize,
          number: pagination.page
        });
    },
    [enroll, filters, sortBy, pagination]
  );

  const unenrollAction = useCallback(
    ua => {
      return () =>
        unenroll(ua, filters, sortBy, {
          size: pagination.pageSize,
          number: pagination.page
        });
    },
    [unenroll, filters, sortBy, pagination]
  );

  const rows = useMemo(() => {
    return (
      courses &&
      courses.map(({ course, ua }) => ({
        id: course.id,
        iltType: <CourseTypeIcon type={course.locationTypeString} />,
        name: course.name,
        location: makeLocationString(course, globalT),
        dateTime: course.dateTime,
        comments: makeCommentFromUa(ua, course, t),
        action: (
          <ActionButton
            ua={ua}
            course={course}
            enroll={enrollAction(ua, course)}
            unenroll={unenrollAction(ua)}
          />
        )
      }))
    );
  }, [courses, enrollAction, unenrollAction]);

  const stateChange = useCallback(
    state => {
      if (state.filters && !isEqual(state.filters, filters)) {
        setFilters(state.filters);
      }

      const sorter = state.sortBy && state.sortBy[0];

      if (sorter) {
        const sortString = (sorter.desc ? "-" : "") + sorter.id;
        const newSortArray = [
          sortString,
          ...sortBy.filter(
            item => ![sorter.id, "-" + sorter.id].includes(sortString)
          )
        ];
        if (JSON.stringify(newSortArray) !== JSON.stringify(sortBy)) {
          setSortBy(newSortArray);
        }
      }
    },
    [sortBy, filters]
  );

  return (
    <>
      <Table
        data={rows || []}
        columns={memoColumns}
        manualFilters={true}
        manualSortBy={true}
        showSelectAllCheckbox={true}
        onStateChange={stateChange}
        paginationState={pagination}
        paginated={true}
        loading={currentAction !== null}
      />
    </>
  );
};

const EmployeeIltsContainer = ({ employeeId }) => {
  const dispatch = useDispatch();
  const { getIlts, enroll, unenroll } = useIlts(employeeId);
  const t = useScopedTranslation("employees.ilt");

  const apiCallbacks = useMemo(
    () => ({
      getIlts,
      enroll: async (...params) => {
        const result = await enroll(...params);
        dispatch(
          enqueueSuccessSnackbar({ message: t("snackbar.enrollSuccessMsg") })
        );
        return result;
      },
      unenroll: async (...params) => {
        const result = await unenroll(...params);
        dispatch(
          enqueueSuccessSnackbar({ message: t("snackbar.unenrollSuccessMsg") })
        );
        return result;
      }
    }),
    []
  );

  return <IltTable api={apiCallbacks} />;
};

export default EmployeeIltsContainer;
